import {
  AccessFragment,
  OtpFragment,
  Permissions,
  ProfileFragment,
  RefreshAccessFragment,
  Roles,
} from "../../models/autogenerated-deliveryService-gql/graphql";
import { User } from "../../models/user";

export enum UserActionTypes {
  SetOtpResponse = "SET_OTP_RESPONSE",
  Login = "LOGIN",
  RefreshAccess = "REFRESH_ACCESS",
  Logout = "LOGOUT",
  SetProfile = "SET_PROFILE",
  SetRole = "SET_ROLE",
}

interface IUserActionSetOtpResponse {
  type: UserActionTypes.SetOtpResponse;
  value: OtpFragment;
}
interface IUserActionLogin {
  type: UserActionTypes.Login;
  value: AccessFragment;
}

interface IUserActionRefreshAccess {
  type: UserActionTypes.RefreshAccess;
  value: RefreshAccessFragment;
}

interface IUserActionSetProfile {
  type: UserActionTypes.SetProfile;
  value: ProfileFragment;
}

interface IUserActionLogout {
  type: UserActionTypes.Logout;
}

interface IUserActionSetRole {
  type: UserActionTypes.SetRole;
  value: Roles;
}

export const nullUser: User = {
  otpResponse: null,
  accessToken: null,
  refreshToken: null,
  role: null,
  permissions: [],
  owners: [],
  isCustomer: false,
  canViewStock: false,
  canViewOutboundDeliveries: false,
  profile: null,
};

export type IUserAction =
  | IUserActionSetOtpResponse
  | IUserActionLogin
  | IUserActionRefreshAccess
  | IUserActionLogout
  | IUserActionSetProfile
  | IUserActionSetRole;

const updateLocalStorageTokens = (
  accessToken: string,
  refreshToken: string,
) => {
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
};

const clearLocalStorageTokens = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  window.dispatchEvent(new Event("logOut"));
};

const decodeJwtPayload = (
  token: string | null,
): Record<string, unknown> | null => {
  if (!token) return null;

  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    const decoded = atob(padded);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const normalizeClaimValue = (value: unknown): string[] => {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === "string");
  }
  return [];
};

const extractOwnersFromAccessToken = (accessToken: string | null): string[] => {
  const payload = decodeJwtPayload(accessToken);
  if (!payload) return [];

  const owners = new Set<string>();

  Object.keys(payload).forEach((key) => {
    if (key.toLowerCase().startsWith("owner:")) {
      owners.add(key.slice("owner:".length));
    }
  });

  Object.entries(payload).forEach(([key, value]) => {
    if (!key.toLowerCase().includes("claim")) return;

    normalizeClaimValue(value).forEach((claim) => {
      if (claim.toLowerCase().startsWith("owner:")) {
        owners.add(claim.slice("owner:".length));
      }
    });
  });

  return Array.from(owners).filter((owner) => owner.trim().length > 0);
};

const isCustomerRole = (role: Roles | null): boolean =>
  role === Roles.Customer || role === Roles.SupplierAndCustomer;

const canViewStock = (permissions: Permissions[]): boolean =>
  permissions.includes(Permissions.CanViewStock);

const canViewOutboundDeliveries = (permissions: Permissions[]): boolean =>
  permissions.includes(Permissions.CanViewOutboundDelivery);

const buildUserFromAccess = (
  state: User,
  access: AccessFragment | RefreshAccessFragment,
): User => {
  const owners = extractOwnersFromAccessToken(access.accessToken);
  const customer = isCustomerRole(access.role);

  return {
    otpResponse: state.otpResponse,
    profile: state.profile,
    ...access,
    owners,
    isCustomer: customer,
    canViewStock: canViewStock(access.permissions),
    canViewOutboundDeliveries: canViewOutboundDeliveries(access.permissions),
  };
};

export const userReducer = (state: User, action: IUserAction): User => {
  switch (action.type) {
    case UserActionTypes.SetOtpResponse:
      return { ...state, otpResponse: action.value };
    case UserActionTypes.SetProfile:
      return { ...state, profile: action.value };
    case UserActionTypes.Login:
      updateLocalStorageTokens(
        action.value.accessToken,
        action.value.refreshToken,
      );
      return buildUserFromAccess(state, action.value);
    case UserActionTypes.RefreshAccess:
      updateLocalStorageTokens(
        action.value.accessToken,
        action.value.refreshToken,
      );
      return buildUserFromAccess(state, action.value);
    case UserActionTypes.Logout:
      clearLocalStorageTokens();
      return nullUser;
    case UserActionTypes.SetRole: {
      const role = action.value;
      return {
        ...state,
        role,
        isCustomer: isCustomerRole(role),
        canViewStock: canViewStock(state.permissions),
        canViewOutboundDeliveries: canViewOutboundDeliveries(state.permissions),
      };
    }
    default:
      return state;
  }
};
